import React, { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Call } from "@twilio/voice-sdk";
import { OverlayPanel } from "primereact/overlaypanel";
import {
	CallPresenterEngagementForm,
	CallPresenterProps,
} from "../../models/components/call-presenter.model";
import UiButton from "../button/Button";
import { Formatter } from "../../services/formatter/formatter.service";
import UiExtensionPad from "../extension-pad/ExtensionPad.component";
import { Form } from "react-final-form";
import { FormApi } from "final-form";
import UiSelect from "../select/Select.component";
import {
	eEngagementDetails,
	engagementType,
	IEngagement,
} from "../../models/entities/engagement.model";
import { getSessionStorageUser } from "../../services/session/auth.service";
import { List, ListOption } from "../../models/misc.model";
import UiInputText from "../input-text/InputText.component";
import { DropdownChangeEvent } from "primereact/dropdown";
import { Validations } from "../../services/form/validations.service";
import { EndpointsService } from "../../services/endpoints/endpoints.service";
import { AxiosError } from "axios";
import { mappedAxiosError } from "../../services/http/http.service";
import UiFieldMessage from "../field-message/FieldMessage.component";
import UiLink from "../link/Link.component";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import UiCalendar from "../calendar/Calendar.component";
import { Calendar } from "primereact/calendar";
import { MenuItem } from "primereact/menuitem";
import { ParticipantContextProps } from "../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../contexts/Participant.context";
import UiModalNextReachoutDate from "../modal-next-reachout-date/ModalNextReachoutDate.component";
import { UiModalNextReachoutDateProps } from "../../models/components/modal-next-reachout-date.model";
import UiInputDate from "../input-date/InputDate.component";

const CallPresenter = (props: CallPresenterProps) => {
	const { t } = useTranslation("common");
	const matomo = useMatomo();
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);

	// Keypad popup
	const op = useRef<OverlayPanel>(null);

	/* Engagement form */

	// Participants (when multiple particiants have the same phone number)
	const [participantList, setParticipantList] = useState<List>([]);
	const [selectedParticipant, setSelectedParticipant] = useState<string | null>(null);

	const [engagementForm, setEngagementForm] = useState<CallPresenterEngagementForm>({
		participant: null,
		outcome: null,
		details: null,
		other: null,
		formMessage: undefined,
		nextReachoutDate: null,
	});

	// Form API ref for updating field values
	const formApiRef = useRef<FormApi<
		CallPresenterEngagementForm,
		Partial<CallPresenterEngagementForm>
	> | null>(null);

	// KAN-2331: the participant's stored next reach-out date at load. On submit we PATCH the
	// date only when the coach actually changed it — the next-RO is decoupled from the engagement
	// create (a module that changes two things makes two calls).
	const initialNextReachoutDateRef = useRef<Date | null>(null);

	// KAN-2343: tracks whether the coach has picked-and-confirmed a next-reachout-date edit
	// (set on confirm in the modal); never reset within a call, so stale fetches can't
	// overwrite an unsaved pick or clobber the change baseline.
	const nextReachoutDateEditedRef = useRef(false);

	// Next reachout date modal state
	const [nextReachoutDateModal, setNextReachoutDateModal] =
		useState<UiModalNextReachoutDateProps>({
			isVisible: false,
			initialDate: null,
			onComplete: (date: Date | null) => {
				nextReachoutDateEditedRef.current = true;
				setEngagementForm((prev) => ({
					...prev,
					nextReachoutDate: date,
				}));
				setNextReachoutDateModal((prev) => ({ ...prev, isVisible: false }));
			},
			onCancel: () => {
				setNextReachoutDateModal((prev) => ({ ...prev, isVisible: false }));
			},
		});

	const outcomeList: List = [
		{ id: true, label: "UI_COMPONENTS.CALL_MANAGEMENT.ENGAGEMENT.outcomeList.true" },
		{ id: false, label: "UI_COMPONENTS.CALL_MANAGEMENT.ENGAGEMENT.outcomeList.false" },
	];
	// Details: The coach talked to the participant
	const detailsList1: List = [
		{ id: "COACHING_SESSION", label: "ENUMS.ENGAGEMENT_DETAILS.COACHING_SESSION" },
		{ id: "WELLNESS_CHECK", label: "ENUMS.ENGAGEMENT_DETAILS.WELLNESS_CHECK" },
		{ id: "NURSE_VISIT", label: "ENUMS.ENGAGEMENT_DETAILS.NURSE_VISIT" },
		{ id: "TECH_SUPPORT", label: "ENUMS.ENGAGEMENT_DETAILS.TECH_SUPPORT" },
		{ id: "EM_VISIT", label: "ENUMS.ENGAGEMENT_DETAILS.EM_VISIT" },
		{ id: "RESCHEDULED", label: "ENUMS.ENGAGEMENT_DETAILS.RESCHEDULED" },
		{ id: "PT_UNENROLLED", label: "ENUMS.ENGAGEMENT_DETAILS.PT_UNENROLLED" },
		{ id: "CONNECTION_ISSUES", label: "ENUMS.ENGAGEMENT_DETAILS.CONNECTION_ISSUES" },
		{ id: "PREVISIT_SETUP_CALL", label: "ENUMS.ENGAGEMENT_DETAILS.PREVISIT_SETUP_CALL" },
		{ id: "FOLLOW_UP_VISIT", label: "ENUMS.ENGAGEMENT_DETAILS.FOLLOW_UP_VISIT" },
		{ id: "INTRO_CALL", label: "ENUMS.ENGAGEMENT_DETAILS.INTRO_CALL" },
		{ id: "INFO_CALL", label: "ENUMS.ENGAGEMENT_DETAILS.INFO_CALL" },
		{ id: "CARE_COORDINATION", label: "ENUMS.ENGAGEMENT_DETAILS.CARE_COORDINATION" },
		{
			id: "POST_DISCHARGE_PROGRAM_COMPLETION",
			label: "ENUMS.ENGAGEMENT_DETAILS.POST_DISCHARGE_PROGRAM_COMPLETION",
		},
		{ id: "CRITICAL_ALERT", label: "ENUMS.ENGAGEMENT_DETAILS.CRITICAL_ALERT" },
		{ id: "OTHER", label: "ENUMS.ENGAGEMENT_DETAILS.OTHER" },
	];
	// Details: The coach didn't talk to tthe participant
	const detailsList2: List = [
		{ id: "LEFT_VOICEMAIL", label: "ENUMS.ENGAGEMENT_DETAILS.LEFT_VOICEMAIL" },
		{ id: "VOICEMAIL_PROBLEM", label: "ENUMS.ENGAGEMENT_DETAILS.VOICEMAIL_PROBLEM" },
		{ id: "INCORRECT_PHONE", label: "ENUMS.ENGAGEMENT_DETAILS.INCORRECT_PHONE" },
		{ id: "OTHER", label: "ENUMS.ENGAGEMENT_DETAILS.OTHER" },
	];

	const openNextReachoutDateModal = () => {
		const initialDate = engagementForm.nextReachoutDate
			? engagementForm.nextReachoutDate instanceof Date
				? engagementForm.nextReachoutDate
				: new Date(engagementForm.nextReachoutDate)
			: null;
		setNextReachoutDateModal((prev) => ({
			...prev,
			isVisible: true,
			initialDate: initialDate,
		}));
	};

	// Sync engagementForm.nextReachoutDate with form field when it changes
	useEffect(() => {
		if (formApiRef.current && engagementForm.nextReachoutDate !== undefined) {
			const dateString = engagementForm.nextReachoutDate
				? Formatter.date(engagementForm.nextReachoutDate.toISOString()) || ""
				: "";
			const currentFieldValue =
				formApiRef.current.getFieldState("nextReachoutDate")?.value || "";

			if (currentFieldValue !== dateString) {
				formApiRef.current.change("nextReachoutDate", dateString as any);
			}
		}
	}, [engagementForm.nextReachoutDate]);

	// Listen the participant information changes
	useEffect(() => {
		updateParticipantInfo();
	}, [props.callPresenter.state.callerInfo]);

	useEffect(() => {
		if (selectedParticipant) {
			let cancelled = false;

			// Fetch participant reachout date
			const participantId = Number(selectedParticipant)
				? Number(selectedParticipant)
				: selectedParticipant.substring(3);
			// If participantId is not a number, its a string remove the first 3 letters
			const participantIdWithoutPrefix = isNaN(participantId as number)
				? Number(selectedParticipant.substring(3))
				: (participantId as number);
			EndpointsService.dataRetriever
				.getParticipantById({ participantId: participantIdWithoutPrefix })
				.then((response) => {
					// Ignore stale responses from a previous selection or keystroke
					if (cancelled) return;
					// Baseline always tracks the selected patient's stored date;
					// the displayed value only follows the fetch when the coach hasn't picked yet.
					const dateValue = response.nextReachoutDate
						? Formatter.dateISOToUTC(response.nextReachoutDate)
						: null;
					initialNextReachoutDateRef.current = dateValue;
					if (!nextReachoutDateEditedRef.current) {
						setEngagementForm((prev) => ({
							...prev,
							nextReachoutDate: dateValue,
						}));
					}
				})
				.catch((error: AxiosError) => {
					// Do nothing
				});

			return () => {
				cancelled = true;
			};
		}
	}, [selectedParticipant]);

	/**
	 * Actions
	 */

	const done = (): void => props.callPresenter.done();
	const extension = (digits: string): void => {
		if (props.callPresenter.state.call && digits)
			props.callPresenter.extension(props.callPresenter.state.call, digits);
	};

	/**
	 * Template: Actions
	 */

	const setActionsActiveTemplate = (): React.ReactNode => {
		return (
			<>
				<div className="call-actions">
					<UiButton
						type="button"
						label="UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.KEYPAD"
						className="p-button-white p-button-sm"
						onClick={(e) => op.current?.toggle(e)}
					></UiButton>
					<UiButton
						type="button"
						label={
							props.callPresenter.state.call?.isMuted()
								? "UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.UNMUTE"
								: "UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.MUTE"
						}
						className="p-button-white p-button-sm"
						icon={
							props.callPresenter.state.call?.isMuted()
								? "pi pi-volume-up"
								: "pi pi-volume-off"
						}
						onClick={props.callPresenter.mute}
					></UiButton>
					<UiButton
						type="button"
						label="UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.HANGUP"
						className="p-button-danger p-button-sm"
						onClick={() => {
							props.callPresenter.hangUp();
							matomo.trackEvent({ category: "CALL_ACTION", action: "HANG_UP" });
						}}
					></UiButton>
				</div>

				{/* Extensionpad */}
				<OverlayPanel
					ref={op}
					dismissable
				>
					{/* {data?.call &&  */}
					<UiExtensionPad
						call={props.callPresenter.state.call || undefined}
						sendExtension={extension}
					></UiExtensionPad>
					{/* } */}
				</OverlayPanel>
			</>
		);
	};

	const setActionsIncomingTemplate = (): React.ReactNode => {
		return (
			<div className="call-actions">
				<UiButton
					type="button"
					label="UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.VOICEMAIL"
					className="p-button-white p-button-sm"
					onClick={props.callPresenter.reject}
				></UiButton>
				<UiButton
					type="button"
					label="UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.ANSWER"
					className="p-button-success p-button-sm"
					onClick={() => {
						props.callPresenter.answer();
						matomo.trackEvent({ category: "CALL_ACTION", action: "ANSWER" });
					}}
				></UiButton>
			</div>
		);
	};

	const setActionsInactiveTemplate = (): React.ReactNode => {
		return (
			<div className="call-actions">
				<UiButton
					type="button"
					label="UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.HANGUP"
					className="p-button-danger p-button-sm"
					onClick={props.callPresenter.hangUp}
				></UiButton>
			</div>
		);
	};

	const setActionsTemplate = (): React.ReactNode => {
		let template: React.ReactNode = <></>;
		switch (props.callPresenter.state.call?.status()) {
			case Call.State.Pending:
				if (props.callPresenter.state.call?.direction === Call.CallDirection.Incoming)
					template = setActionsIncomingTemplate();
				break;
			case Call.State.Open:
				template = setActionsActiveTemplate();
				break;
			case Call.State.Connecting:
			case Call.State.Reconnecting:
			case Call.State.Ringing:
				template = setActionsInactiveTemplate();
				break;
			default:
				break;
		}
		return template;
	};

	/**
	 * Template: Engagement
	 */

	// Participant information
	const updateParticipantInfo = () => {
		let list: List = [];

		if (props.callPresenter.state.callerInfo?.length) {
			/* We could find the phone number in the participants DB */

			// Update the participants list
			const participants: List = props.callPresenter.state.callerInfo.map((pt) => {
				const item: ListOption = {
					id: pt.id,
					label: `${Formatter.fullName(pt.firstName, pt.lastName)} / ${pt.kannactId}`,
				};
				return item;
			});
			list = participants;
		} else {
			// We couldn't find the phone number in the participants DB
			const phoneNumber: string =
				props.callPresenter.state.call?.direction === Call.CallDirection.Incoming
					? props.callPresenter.state.fromNumber
					: props.callPresenter.state.toNumber;

			list = [{ id: phoneNumber, label: phoneNumber }];
		}

		// Set select options list
		setParticipantList(list);
	};

	// Engagement form: outcome changed
	const outcomeChanged = (event: DropdownChangeEvent): void => {
		// Update field value, and update the fields that depend on this one
		setEngagementForm({
			...engagementForm,
			outcome: event.target.value,
			details: null,
			other: null,
		});
	};

	// Engagement form: details changed
	const detailsChanged = (event: DropdownChangeEvent): void => {
		// Update field value, and update the fields that depend on this one
		setEngagementForm({
			...engagementForm,
			details: event.target.value,
			other: null,
		});
	};

	// Submit: create engagement
	const submitEngagement = async (
		data: CallPresenterEngagementForm,
		form: FormApi<CallPresenterEngagementForm, Partial<CallPresenterEngagementForm>>
	): Promise<void> => {
		const participant = Number(data.participant)
			? data.participant
			: data.participant?.toString().substring(3);
		await EndpointsService.dataRetriever
			.createEngagement({
				body: {
					callSid: props.callPresenter.state.call?.parameters["CallSid"] as string, // CallSid is the parentCallSid at the moment of the call
					callType: engagementType.PHONE,
					coachId: getSessionStorageUser().id!,
					details: data.details,
					patientId: participant as number,
					other: data.other,
					outcome: data.outcome as boolean,
					engagementDate: new Date(),
				},
			})
			.then(async (response: IEngagement) => {
				// KAN-2331: next-RO is decoupled from the engagement. If the coach changed it on the
				// call banner, persist it with its own PATCH (only when it actually changed).
				const serializeNextRo = (d: Date | null) =>
					d ? Formatter.dateUTCWithoutTime(d)!.toISOString() : null;
				const newNextRo = serializeNextRo(engagementForm.nextReachoutDate);
				if (newNextRo !== serializeNextRo(initialNextReachoutDateRef.current)) {
					// updateProfileGeneralInfo's body type doesn't declare nextReachoutDate; the profile
					// modal passes it via spread (excess-property check is bypassed for spreads), so we
					// mirror that established pattern here.
					const nextRoBody: { nextReachoutDate: string | null } = {
						nextReachoutDate: newNextRo,
					};
					await EndpointsService.dataRetriever
						.updateProfileGeneralInfo({
							body: {
								participantId: participant as number,
								...nextRoBody,
							},
						})
						.catch((error: AxiosError) => {
							// The engagement is already logged; a failed date update shouldn't block the flow.
							console.error("Error updating next reachout date:", error);
						});
				}
				// Refresh participant context to update the UI
				if (participantContext?.onRefresh) {
					await participantContext.onRefresh();
				}
				done();
			})
			.catch((error: AxiosError) => {
				// Error message
				setEngagementForm({
					...engagementForm,
					formMessage: mappedAxiosError(
						undefined,
						"UI_COMPONENTS.CALL_MANAGEMENT.MESSAGES.SUBMITTING_ERROR"
					),
				});
			});
	};

	// Template: engagement
	const setEngagementTemplate = (): React.ReactNode => {
		return (
			<>
				{props.callPresenter.state.call?.status() === Call.State.Closed && (
					<Form
						onSubmit={submitEngagement}
						initialValues={{
							...engagementForm,
							nextReachoutDate: engagementForm.nextReachoutDate
								? (Formatter.date(
										engagementForm.nextReachoutDate.toISOString()
									) as any)
								: "",
						}}
						render={({ handleSubmit, form, submitting }) => {
							// Store form API ref
							formApiRef.current = form;
							return (
								<form onSubmit={handleSubmit}>
									<div className="call-engagement">
										<div className="row">
											<div className="col-12 col-sm-6 col-md-6 col-lg-3">
												<UiSelect
													id="participant"
													label="UI_COMPONENTS.CALL_MANAGEMENT.ENGAGEMENT.participant"
													name="participant"
													onChange={(e) => {
														setEngagementForm({
															...engagementForm,
															participant: e.target.value,
														});
														setSelectedParticipant(e.target.value);
													}}
													value={participantList[0]}
													disabled={submitting}
													editable={true}
													validations={[Validations.required]}
													options={participantList}
													appendTo="self"
												/>
											</div>

											{/* Call outcome: Did the coach talk to the participant? */}
											<div className="col-12 col-sm-6 col-md-6 col-lg-2">
												<UiSelect
													id="outcome"
													label="UI_COMPONENTS.CALL_MANAGEMENT.ENGAGEMENT.outcome"
													name="outcome"
													onChange={outcomeChanged}
													disabled={submitting}
													validations={[Validations.required]}
													options={outcomeList}
													appendTo="self"
												/>
											</div>

											{/* Details */}
											<div className="col-12 col-sm-6 col-md-6 col-lg-2">
												<UiSelect
													id="details"
													label="UI_COMPONENTS.CALL_MANAGEMENT.ENGAGEMENT.details"
													name="details"
													onChange={detailsChanged}
													disabled={submitting}
													validations={[Validations.required]}
													scrollHeight="350px"
													options={
														engagementForm.outcome === true
															? detailsList1
															: detailsList2
													}
													appendTo="self"
												/>
											</div>

											{/* Other */}
											{engagementForm.details ===
												eEngagementDetails.OTHER && (
												<div className="col-12 col-sm-6 col-md-6 col-lg-3">
													<UiInputText
														id="other"
														label="UI_COMPONENTS.CALL_MANAGEMENT.ENGAGEMENT.other"
														name="other"
														onChange={(e) =>
															setEngagementForm({
																...engagementForm,
																other: e.target.value,
															})
														}
														disabled={submitting}
													/>
												</div>
											)}

											{/* Next reachout date */}
											<div className="col-12 col-sm-6 col-md-6 col-lg-2">
												<div
													onClick={(e) => {
														if (!submitting) {
															e.preventDefault();
															e.stopPropagation();
															openNextReachoutDateModal();
														}
													}}
													onMouseDown={(e) => {
														if (!submitting) {
															e.preventDefault();
															e.stopPropagation();
														}
													}}
													style={{
														cursor: submitting
															? "not-allowed"
															: "pointer",
													}}
												>
													<UiInputText
														id="nextReachoutDate"
														label="UI_COMPONENTS.CALL_MANAGEMENT.ENGAGEMENT.nextReachoutDate"
														name="nextReachoutDate"
														value={
															engagementForm.nextReachoutDate
																? Formatter.dateUTCWithoutTime(
																		engagementForm.nextReachoutDate
																	)!.toISOString()
																: ""
														}
														disabled={submitting}
														validations={[]}
														readOnly={true}
														rightIcon="pi pi-pencil"
														rightIconFn={
															submitting
																? undefined
																: openNextReachoutDateModal
														}
													/>
												</div>
											</div>
										</div>

										<div className="complete-engagement">
											<UiButton
												type="submit"
												label={
													submitting
														? "UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.DONE_SUBMITTING"
														: "UI_COMPONENTS.CALL_MANAGEMENT.ACTIONS.DONE"
												}
												className="p-button-white inline-form"
												loading={submitting}
												disabled={!form.getState().valid}
											></UiButton>
										</div>
									</div>

									{/* Login endpoint messages */}
									{engagementForm?.formMessage && (
										<div className="pt-2 pb-2">
											<UiFieldMessage
												{...engagementForm?.formMessage}
											></UiFieldMessage>
										</div>
									)}
								</form>
							);
						}}
					/>
				)}
			</>
		);
	};

	/**
	 * Template: Information
	 */
	const setInfoTemplate = (): React.ReactNode => {
		// Call status title (CALL ENDED, INCOMING CALL, ACTIVE CALL, etc.)
		const setTitle = (): string => {
			let title: string = "";
			switch (props.callPresenter.state.call?.status()) {
				case Call.State.Closed:
					title = t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.ENDED`);
					break;
				case Call.State.Open:
					title = t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.ACTIVE`);
					break;
				case Call.State.Pending:
					// Used only with incoming calls
					if (props.callPresenter.state.call?.direction === Call.CallDirection.Incoming)
						title = t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.INCOMING`);
					break;
				case Call.State.Ringing:
					// Used only with outgoing calls
					if (props.callPresenter.state.call?.direction === Call.CallDirection.Outgoing)
						title = t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.CALLING`);
					break;
				case Call.State.Connecting:
					title =
						props.callPresenter.state.call?.direction === Call.CallDirection.Incoming
							? t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.CONNECTING_INCOMING`)
							: t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.CONNECTING_OUTBOUND`);
					break;
				case Call.State.Reconnecting:
					title =
						props.callPresenter.state.call?.direction === Call.CallDirection.Incoming
							? t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.RECONNECTING_INCOMING`)
							: t(`UI_COMPONENTS.CALL_MANAGEMENT.TITLES.RECONNECTING_OUTBOUND`);
					break;
				default:
					break;
			}
			return title;
		};

		// Call duration timer
		const setTime = (): string | undefined =>
			Formatter.duration(props.callPresenter.state.timer);

		// Phone number (formatted)
		const setPhone = (): string | undefined =>
			Formatter.phoneNumber(
				props.callPresenter.state.call?.direction === Call.CallDirection.Incoming
					? props.callPresenter.state.fromNumber
					: props.callPresenter.state.toNumber
			);

		return (
			<div className={`call-info`}>
				<span className="title">{setTitle()}</span>
				<span className="duration">{setTime()}</span>
				<span className="phone">{setPhone()}</span>
				{props.callPresenter.state.isConference &&
					props.callPresenter.state.call?.status() === Call.State.Open && (
						<span className="k-conference-badge">
							{t("UI_COMPONENTS.CALL_MANAGEMENT.GROUP_CALL")}
						</span>
					)}
				{props.callPresenter.state.call?.direction === Call.CallDirection.Incoming &&
					props.callPresenter.state.call?.status() !== Call.State.Closed &&
					props.callPresenter.state.callerInfo?.length && (
						<span className="pt-info">
							{/* Brand logo */}
							{props.callPresenter.state.callerInfo[0].brandLogo && (
								<img
									alt="brand logo"
									className="brand-logo"
									src={props.callPresenter.state.callerInfo[0].brandLogo}
								/>
							)}

							{/* KannactID */}
							{props.callPresenter.state.callerInfo[0].kannactId && (
								<UiLink
									className="kannact-id"
									to={`/participant/${props.callPresenter.state.callerInfo[0].kannactId.slice(3, props.callPresenter.state.callerInfo[0].kannactId.length)}/dashboard`}
									label={props.callPresenter.state.callerInfo[0].kannactId}
								/>
							)}
						</span>
					)}
			</div>
		);
	};

	return (
		<>
			{props.callPresenter.state.call?.status() && (
				<div
					className={`k-call-mngt-container direction-${props.callPresenter.state.call?.direction} status-${props.callPresenter.state.call?.status()}`}
				>
					{/* Minimize the call bar */}
					{props.onMinimize && (
						<UiButton
							type="button"
							className="p-button-text neutral k-call-bar__minimize"
							icon="pi pi-chevron-down"
							onClick={props.onMinimize}
						/>
					)}

					<section className="top-section">
						{/* Information: call status + timer (only shown when the call is active) + phone number */}
						{setInfoTemplate()}

						{/* Call actions */}
						{setActionsTemplate()}
					</section>

					<section className="bottom-section">
						{/* Engagement: only shown when the call ends */}
						{setEngagementTemplate()}
					</section>
				</div>
			)}

			{/* Next reachout date modal */}
			{nextReachoutDateModal.isVisible && (
				<UiModalNextReachoutDate {...nextReachoutDateModal} />
			)}
		</>
	);
};

export default CallPresenter;
